import {ElementRef, Injectable} from '@angular/core';
import {Router} from '@angular/router';
import {GtNotificationService} from '@app/services/gt-notification.service';
import {CallControlService} from '@gt-api';
import * as JsSIP from 'jssip';
import {BehaviorSubject, Subject} from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class JsSIPService {
  registered = new BehaviorSubject<boolean>(false);
  onSendingInvite = new Subject<string>();
  onSessionEnded = new Subject<void>();
  onSessionAccepted = new Subject<void>();
  onSessionProgress = new Subject<void>();
  error = new Subject<string>();
  private userAgent;
  private session;
  private callee: string;
  private caller: string;
  onIncomingCall = new BehaviorSubject<string>(null);

  private local: ElementRef;
  private remote: ElementRef;

  private readonly defaultOptions = {
    extraHeaders: ['X-Source-Webrtc: true']
  };
  private readonly errorNoSession = 'No session';
  private readonly errorNotLoggedIn = 'Not logged in';

  constructor(
    private readonly callControlService: CallControlService,
    private readonly notificationService: GtNotificationService,
    private readonly router: Router
  ) {}

  mute() {
    if (this.session) {
      this.session.mute();
    } else {
      this.error.next(this.errorNoSession);
    }
  }

  unMute() {
    if (this.session) {
      this.session.unmute();
    } else {
      this.error.next(this.errorNoSession);
    }
  }

  hold() {
    if (this.session) {
      this.session.hold();
    } else {
      this.error.next(this.errorNoSession);
    }
  }

  unHold() {
    if (this.session) {
      this.session.unhold();
    } else {
      this.error.next(this.errorNoSession);
    }
  }

  call(callee: string): void {
    const options = {
      ...this.defaultOptions,
      mediaConstraints: {audio: true, video: false}
    };

    if (this.userAgent) {
      this.callee = callee;
      const session = this.userAgent.call(callee, options);
      this.handleSession(session);
    } else {
      this.error.next(this.errorNotLoggedIn);
    }
  }

  accept(): void {
    this.session.answer(this.defaultOptions);
  }

  // only the callee can transfer a call
  transfer(transferee: string) {
    this.callControlService
      .transferCall2(undefined, undefined, this.caller, this.userAgent.configuration.uri.user, transferee)
      .subscribe(message => {
        if (message.code !== 200) {
          this.notificationService.errorNotification.next(`Transfer not possible: ${message.message}`);
          return;
        } else if (message.manualHangupRequired) {
          this.hangUp();
        }
        this.notificationService.normalNotification.next(`Transfer successful: ${message.message}`);
      });
  }

  sendDTMF(tone): void {
    if (this.session) {
      this.session.sendDTMF(tone, this.defaultOptions);
    } else {
      this.error.next(this.errorNoSession);
    }
  }

  hangUp(): void {
    if (this.session) {
      this.session.terminate(this.defaultOptions);
    } else {
      this.error.next(this.errorNoSession);
    }
  }

  register(socketURL: string, userURI: string, password: string, local: ElementRef, remote: ElementRef): void {
    // For debugging: JsSIP.debug.enable('*');

    this.local = local;
    this.remote = remote;

    const socket = new JsSIP.WebSocketInterface(socketURL);

    const configuration = {
      sockets: [socket],
      uri: userURI,
      password,
      register: true,
      register_expires: 120
    };

    this.userAgent = new JsSIP.UA(configuration);
    this.userAgent.on('registered', () => this.registered.next(true));

    this.userAgent.on('newRTCSession', data => {
      if (data.originator === 'local') {
        return;
      }

      const incomingSession = data.session;

      if (this.session) {
        incomingSession.terminate({
          status_code: 486,
          reason_phrase: 'Busy Here'
        });
        return;
      }

      this.handleSession(incomingSession);
      this.caller = incomingSession.remote_identity.uri.user;
      this.notificationService.clickableNotification.next({
        message: `Incoming call from ${this.caller}`,
        action: () => {
          this.router.navigate(['phone']);
          this.accept();
        }
      });
      this.onIncomingCall.next(this.caller);
    });

    this.userAgent.start();
  }

  private handleSession(session): void {
    this.session = session;

    this.session.on('accepted', () => {
      console.log('session accepted');

      this.onSessionAccepted.next();
    });

    this.session.on('sending', () => {
      this.onSendingInvite.next(this.callee);
    });

    this.session.on('progress', data => {
      console.log('session progress');
      this.onSessionProgress.next();
    });

    this.session.on('ended', () => {
      console.log('session ended');

      this.cleanUp();
      this.onSessionEnded.next();
    });

    this.session.on('connecting', () => {
      console.log('connecting');

      const peerConnection = session.connection;

      const localStream = peerConnection.getLocalStreams()[0];
      const remoteStream = peerConnection.getRemoteStreams()[0];

      if (localStream) {
        this.local.nativeElement.srcObject = localStream;
        this.local.nativeElement.muted = true;

        console.log('attached local stream');
      }

      if (remoteStream) {
        this.remote.nativeElement.srcObject = remoteStream;
        console.log('attached remote stream');
      }

      peerConnection.addEventListener('addstream', event => {
        console.log('peerconnection "addstream" event');

        this.remote.nativeElement.srcObject = event.stream;
        console.log('attached remote stream');
      });
    });

    this.session.on('failed', data => {
      console.error('session failed');
      console.error(data);

      this.cleanUp();
      this.onSessionEnded.next();
      this.error.next(data.cause);
    });
  }

  private cleanUp() {
    this.callee = '';
    this.caller = undefined;
    this.session = undefined;
  }
}
